import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** Entity row / tlink payload → one-line SG address (Withdraw Director 패턴). */
export function buildAddressFromEntity(ent: Record<string, unknown>): string {
  const blockNo = trim(ent.blockNo ?? ent.BLOCK_NO ?? ent.blkHseNo ?? ent.BLK_HSE_NO);
  const streetName = trim(ent.streetName ?? ent.STREET_NAME);
  const buildingName = trim(ent.buildingName ?? ent.BUILDING_NAME);
  const addrLevel = trim(ent.addrLevel ?? ent.ADDR_LEVEL ?? ent.levelNo ?? ent.LEVEL_NO);
  const addrUnit = trim(ent.addrUnit ?? ent.ADDR_UNIT ?? ent.unitNo ?? ent.UNIT_NO);
  const postalCode = trim(ent.postalCode ?? ent.POSTAL_CODE);

  const line1 = [blockNo, streetName].filter(Boolean).join(' ').trim();
  const line2 = [addrLevel, addrUnit].filter(Boolean).join('-');
  const unit = line2 ? `#${line2}` : '';
  const assembled = [line1, unit, buildingName, postalCode ? `SINGAPORE ${postalCode}` : postalCode]
    .filter(Boolean)
    .join(', ')
    .trim();
  if (assembled) return assembled;

  return trim(
    ent.companyAddress
    ?? ent.address
    ?? ent.registeredOfficeAddress
    ?? ent.registered_office_address
    ?? ent.ADDRESS
    ?? ent.ADDR,
  );
}

async function fetchEntityAddressByApi(entityCd: string): Promise<string> {
  const tryFetch = async (url: string): Promise<unknown | null> => {
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  };

  const extract = (payload: unknown): string => {
    const p = payload as Record<string, unknown>;
    const candidates = [
      p,
      p?.data,
      (p?.data as Record<string, unknown>)?.data,
      (p?.data as Record<string, unknown>)?.item,
      p?.item,
      p?.profile,
      p?.entity,
      p?.company,
      p?.registeredAddress,
      p?.address,
    ].filter(Boolean) as Record<string, unknown>[];
    for (const c of candidates) {
      const built = buildAddressFromEntity(c);
      if (built) return built;
    }
    return '';
  };

  const endpoints = [
    `/api/startTask/common/entity/${encodeURIComponent(entityCd)}`,
    `/api/entity/${encodeURIComponent(entityCd)}`,
    `/api/entity/profile/${encodeURIComponent(entityCd)}`,
  ];
  for (const url of endpoints) {
    const payload = await tryFetch(url);
    if (!payload) continue;
    const address = extract(payload);
    if (address) return address;
  }
  return '';
}

/** 현재 선택 entity 의 등록 주소 (Reply-to address 기본값). */
export async function resolveSgEntityRegisteredAddress(
  ent: Record<string, unknown> | null | undefined,
  entityCd: string,
): Promise<string> {
  const fromEnt = ent ? buildAddressFromEntity(ent) : '';
  if (fromEnt) return fromEnt;

  const ec = trim(entityCd);
  if (!ec) return '';

  try {
    const profile = await fetchEntityAddresses(ec);
    const registered = trim(profile?.registered);
    if (registered) return registered;
  } catch {
    /* ignore */
  }

  return fetchEntityAddressByApi(ec);
}
